"use client";

import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { type LucideIcon, XIcon } from "lucide-react";
import {
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  type MouseEventHandler,
  useContext,
} from "react";

import { Button } from "../button/button";
import { cn } from "../../lib/utils";

type BannerContextProps = {
  show: boolean;
  setShow: (show: boolean) => void;
};

const BannerContext = createContext<BannerContextProps>({
  show: true,
  setShow: () => {},
});

export type BannerProps = HTMLAttributes<HTMLDivElement> & {
  visible?: boolean;
  defaultVisible?: boolean;
  onClose?: () => void;
  inset?: boolean;
};

function Banner({
  children,
  visible,
  defaultVisible = true,
  onClose,
  className,
  inset = false,
  ...props
}: BannerProps) {
  const [show, setShow] = useControllableState({
    defaultProp: defaultVisible,
    prop: visible,
    onChange: onClose,
  });

  if (!show) {
    return null;
  }

  return (
    <BannerContext.Provider value={{ show, setShow }}>
      <div
        className={cn(
          "flex items-center gap-2 bg-primary px-3 py-1.5 text-primary-foreground",
          inset ? "w-fit max-w-full rounded-lg shadow-sm" : "w-full justify-between gap-3 px-4 py-2",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </BannerContext.Provider>
  );
}

export type BannerIconProps = HTMLAttributes<HTMLDivElement> & {
  icon: LucideIcon;
};

function BannerIcon({ icon: Icon, className, ...props }: BannerIconProps) {
  return (
    <div
      className={cn(
        "rounded-full border border-background/20 bg-background/10 p-1 shadow-sm",
        className
      )}
      {...props}
    >
      <Icon size={16} />
    </div>
  );
}

export type BannerTitleProps = HTMLAttributes<HTMLParagraphElement>;

function BannerTitle({ className, ...props }: BannerTitleProps) {
  return <p className={cn("text-sm", className)} {...props} />;
}

export type BannerActionProps = ComponentProps<typeof Button>;

function BannerAction({
  variant = "outline",
  size = "sm",
  className,
  ...props
}: BannerActionProps) {
  return (
    <Button
      className={cn("shrink-0", className)}
      size={size}
      variant={variant}
      {...props}
    />
  );
}

export type BannerCloseProps = ComponentProps<typeof Button>;

function BannerClose({
  variant = "ghost",
  size = "xs",
  onClick,
  className,
  ...props
}: BannerCloseProps) {
  const { setShow } = useContext(BannerContext);

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    setShow(false);
    onClick?.(e);
  };

  return (
    <Button
      className={cn(
        "shrink-0 bg-transparent hover:bg-background/10 hover:text-background",
        className
      )}
      onClick={handleClick}
      size={size}
      variant={variant}
      {...props}
    >
      <XIcon size={18} />
    </Button>
  );
}

export { Banner, BannerAction, BannerClose, BannerIcon, BannerTitle };
